import './styles.css';
import { CinematicEngine, installDeterministicRenderBridge, timelineStateAt, type QualityTier } from './engine';
import { filmManifest, type Chapter, type DialogueCue, type SourceClassification } from './story';
import { MasterAudioPlayer } from './runtimeAudio';

const app = document.querySelector<HTMLElement>('#app');
if (!app) throw new Error('Missing #app root.');

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const storedQuality = localStorage.getItem('veil-quality') as QualityTier | null;
const initialQuality: QualityTier = storedQuality && ['low', 'medium', 'high'].includes(storedQuality)
  ? storedQuality
  : window.devicePixelRatio > 1.5 ? 'high' : 'medium';

const timecode = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
};

app.innerHTML = `
  <section class="film-shell controls-visible" aria-label="Cinematic player">
    <div class="fallback-poster" aria-hidden="true"></div>
    <canvas class="film-canvas" aria-label="Animated cinematic scene"></canvas>

    <div class="chrome">
      <header class="topbar">
        <div class="brand"><strong>The Veil and the Garden</strong><span>A cinematic meditation</span></div>
        <button class="text-button chapters-toggle" type="button" aria-expanded="false" aria-controls="film-drawer">Chapters & notes</button>
      </header>

      <section class="chapter-card" aria-live="polite">
        <div class="eyebrow"></div><h2></h2><p></p>
      </section>
      <div class="source-pill" role="status" aria-live="polite" aria-atomic="true"></div>

      <section class="closing-card" aria-label="Sources and interpretation notes">
        <div class="closing-mark">✦</div>
        <h2>Life was not taken from the world.<br />The world was opened to life.</h2>
        <div class="closing-sources">
          <h3>Sources &amp; interpretation</h3>
          ${filmManifest.closingCard.map((line) => `<p>${line}</p>`).join('')}
        </div>
      </section>

      <div class="subtitles" aria-live="polite" aria-atomic="true">
        <div class="subtitle-speaker"></div><div class="subtitle-text"></div>
      </div>

      <section class="transport" aria-label="Film controls">
        <div class="timeline-row">
          <span class="time current-time">0:00</span>
          <label class="sr-only" for="film-timeline">Film timeline</label>
          <input id="film-timeline" class="timeline" type="range" min="0" max="${filmManifest.runtimeSeconds}" value="0" step="0.01" aria-label="Film timeline" />
          <span class="time total-time">${timecode(filmManifest.runtimeSeconds)}</span>
        </div>
        <div class="control-row">
          <button class="icon-button play-toggle" type="button" aria-label="Play film">▶</button>
          <button class="icon-button mute-toggle" type="button" aria-label="Mute audio" aria-pressed="false">♪</button>
          <button class="text-button captions-toggle" type="button" aria-label="Toggle captions" aria-pressed="true">CC</button>
          <div class="chapter-label">I · The Veil</div>
          <div class="spacer"></div>
          <label class="sr-only" for="quality">Rendering quality</label>
          <select id="quality" class="quality-select" aria-label="Rendering quality">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
          <button class="text-button motion-toggle" type="button" aria-label="Toggle reduced motion" aria-pressed="${prefersReduced}">Steady camera</button>
          <button class="icon-button fullscreen-toggle" type="button" aria-label="Enter fullscreen">⛶</button>
        </div>
      </section>
    </div>

    <section class="opening" aria-labelledby="film-title">
      <div class="opening-content">
        <div class="opening-kicker">A dramatic theological interpretation</div>
        <h1 id="film-title">The Veil<br />and the Garden</h1>
        <p class="subtitle">A cinematic meditation on the Passion and Resurrection</p>
        <p class="notice">The canonical Passion narratives are joined by clearly marked symbolic visions. This film is not a documentary reconstruction. Contains restrained depictions of crucifixion and grief.</p>
        <button class="begin-button" type="button" aria-label="Play film">Enter the story</button>
        <div class="runtime-note">8 min 10 sec · Captions on · Headphones recommended</div>
      </div>
    </section>

    <aside class="drawer" id="film-drawer" aria-label="Chapters and production notes" aria-hidden="true">
      <button class="icon-button drawer-close" type="button" aria-label="Close chapters">×</button>
      <h2>Chapters</h2>
      <nav class="chapter-list" aria-label="Film chapters"></nav>
      <section class="drawer-section">
        <h3>About this interpretation</h3>
        <p>${filmManifest.openingCard.join(' ')}</p>
        <p>The symbolic layer treats fog as ignorance, shadow as domination, and living light as recognition. Later mystical writings inform imagery rather than replacing the canonical story.</p>
      </section>
      <section class="drawer-section">
        <h3>Historical care</h3>
        <p>Jesus, his family, disciples, and earliest followers are presented within first-century Jewish life. Roman authority performs the execution. The film rejects collective blame against Jewish people then or now.</p>
      </section>
      <section class="drawer-section">
        <h3>Source key</h3>
        <ul><li>CANON — tied to a canonical passage</li><li>COMPOSITE — a declared Gospel harmonization</li><li>VISION — original or later-text-inspired interpretation</li></ul>
      </section>
    </aside>
  </section>
`;

const shell = app.querySelector<HTMLElement>('.film-shell')!;
const canvas = app.querySelector<HTMLCanvasElement>('canvas')!;
const opening = app.querySelector<HTMLElement>('.opening')!;
const poster = app.querySelector<HTMLElement>('.fallback-poster')!;
const playButton = app.querySelector<HTMLButtonElement>('.play-toggle')!;
const beginButton = app.querySelector<HTMLButtonElement>('.begin-button')!;
const transport = app.querySelector<HTMLElement>('.transport')!;
const timeline = app.querySelector<HTMLInputElement>('.timeline')!;
const currentTimeLabel = app.querySelector<HTMLElement>('.current-time')!;
const chapterLabel = app.querySelector<HTMLElement>('.chapter-label')!;
const chapterCard = app.querySelector<HTMLElement>('.chapter-card')!;
const chapterEyebrow = chapterCard.querySelector<HTMLElement>('.eyebrow')!;
const chapterTitle = chapterCard.querySelector<HTMLElement>('h2')!;
const chapterSubtitle = chapterCard.querySelector<HTMLElement>('p')!;
const sourcePill = app.querySelector<HTMLElement>('.source-pill')!;
const closingCard = app.querySelector<HTMLElement>('.closing-card')!;
const subtitles = app.querySelector<HTMLElement>('.subtitles')!;
const subtitleSpeaker = app.querySelector<HTMLElement>('.subtitle-speaker')!;
const subtitleText = app.querySelector<HTMLElement>('.subtitle-text')!;
const muteButton = app.querySelector<HTMLButtonElement>('.mute-toggle')!;
const captionsButton = app.querySelector<HTMLButtonElement>('.captions-toggle')!;
const motionButton = app.querySelector<HTMLButtonElement>('.motion-toggle')!;
const qualitySelect = app.querySelector<HTMLSelectElement>('.quality-select')!;
const fullscreenButton = app.querySelector<HTMLButtonElement>('.fullscreen-toggle')!;
const drawer = app.querySelector<HTMLElement>('.drawer')!;
const drawerToggle = app.querySelector<HTMLButtonElement>('.chapters-toggle')!;
const drawerClose = app.querySelector<HTMLButtonElement>('.drawer-close')!;
const chapterList = app.querySelector<HTMLElement>('.chapter-list')!;
drawer.inert = true;

qualitySelect.value = initialQuality;
chapterList.innerHTML = filmManifest.chapters.map((chapter) => `
  <button class="chapter-button" type="button" data-time="${chapter.start}" aria-current="false">
    <span class="num">${String(chapter.index).padStart(2, '0')}</span>
    <span class="name">${chapter.title}</span>
    <span class="stamp">${timecode(chapter.start)}</span>
  </button>
`).join('');

let engine: CinematicEngine;
let webglAvailable = true;
try {
  engine = new CinematicEngine({ canvas, quality: initialQuality, reducedMotion: prefersReduced });
} catch (error) {
  webglAvailable = false;
  poster.classList.remove('is-hidden');
  beginButton.textContent = 'Read with captions';
  console.warn('WebGL cinematic unavailable; poster and transcript controls remain active.', error);
  engine = {
    duration: filmManifest.runtimeSeconds,
    whenReady: Promise.resolve(),
    seekFilmTime: async () => undefined,
    render: () => undefined,
    resize: () => undefined,
    setQuality: () => undefined,
    setReducedMotion: () => undefined,
    diagnostics: () => ({ triangles: 0, geometries: 0, textures: 0, programs: 0, quality: initialQuality }),
    dispose: () => undefined,
  } as unknown as CinematicEngine;
}

const removeRenderBridge = installDeterministicRenderBridge(engine);
const masterAudio = new MasterAudioPlayer();
masterAudio.initialize();

let currentTime = 0;
let playing = false;
let muted = false;
let captionsEnabled = true;
let captionKeyboardActive = false;
let reducedMotion = prefersReduced;
let playAnchorTime = 0;
let playAnchorClock = performance.now();
let activeChapterIndex = -1;
let controlsTimer = 0;
const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;

const chapterAt = (seconds: number): Chapter => filmManifest.chapters.find((chapter) => seconds >= chapter.start && seconds < chapter.end)
  ?? filmManifest.chapters[filmManifest.chapters.length - 1]!;
const dialogueAt = (seconds: number): DialogueCue | undefined => {
  const chapter = chapterAt(seconds);
  return chapter.dialogue.find((cue) => seconds >= cue.start && seconds < cue.end);
};

function resize(): void {
  engine.resize(shell.clientWidth, shell.clientHeight, false);
  void engine.seekFilmTime(currentTime);
}

function setDrawer(open: boolean): void {
  drawer.classList.toggle('is-open', open);
  drawer.setAttribute('aria-hidden', String(!open));
  drawer.inert = !open;
  drawerToggle.setAttribute('aria-expanded', String(open));
  if (open) drawerClose.focus();
  else if (drawer.contains(document.activeElement)) drawerToggle.focus();
}

function scheduleControlsHide(delay = coarsePointer ? 2200 : 3000): void {
  window.clearTimeout(controlsTimer);
  controlsTimer = window.setTimeout(() => {
    const keyboardFocusInChrome = shell.classList.contains('keyboard-navigation')
      && shell.querySelector('.chrome')?.contains(document.activeElement);
    const activelyUsingChrome = keyboardFocusInChrome;
    if (playing && !drawer.classList.contains('is-open') && !activelyUsingChrome) shell.classList.remove('controls-visible');
    else if (playing) scheduleControlsHide(delay);
  }, delay);
}

function showControls(hideDelay?: number): void {
  shell.classList.add('controls-visible');
  scheduleControlsHide(hideDelay);
}

function seek(seconds: number): void {
  currentTime = Math.min(filmManifest.runtimeSeconds, Math.max(0, seconds));
  playAnchorTime = currentTime;
  playAnchorClock = performance.now();
  void engine.seekFilmTime(currentTime);
  masterAudio.seek(currentTime);
  updateInterface();
}

async function setPlaying(next: boolean): Promise<void> {
  if (next && currentTime >= filmManifest.runtimeSeconds) seek(0);
  playing = next;
  playAnchorTime = currentTime;
  playAnchorClock = performance.now();
  if (!playing) masterAudio.pause();
  playButton.textContent = playing ? 'Ⅱ' : '▶';
  playButton.setAttribute('aria-label', playing ? 'Pause film' : 'Play film');
  shell.classList.toggle('is-playing', playing);
  if (playing) showControls();
  else {
    window.clearTimeout(controlsTimer);
    shell.classList.add('controls-visible');
  }
  updateInterface();
}

function setMuted(next: boolean): void {
  muted = next;
  masterAudio.setMuted(muted);
  muteButton.textContent = muted ? '×' : '♪';
  muteButton.setAttribute('aria-pressed', String(muted));
  muteButton.setAttribute('aria-label', muted ? 'Unmute audio' : 'Mute audio');
}

function sourceLabel(classification: SourceClassification): string {
  return classification === 'CANON' ? 'Canonical source' : classification === 'COMPOSITE' ? 'Gospel composite' : 'Interpretive vision';
}

function updateInterface(): void {
  timeline.value = String(currentTime);
  currentTimeLabel.textContent = timecode(currentTime);
  const chapter = chapterAt(currentTime);
  const chapterLocal = currentTime - chapter.start;
  chapterLabel.textContent = `${chapter.card.eyebrow} · ${chapter.title}`;
  if (chapter.index !== activeChapterIndex) {
    activeChapterIndex = chapter.index;
    chapterEyebrow.textContent = `Chapter ${chapter.card.eyebrow}`;
    chapterTitle.textContent = chapter.card.title;
    chapterSubtitle.textContent = chapter.card.subtitle;
  }
  const closingVisible = currentTime >= 483;
  const chapterCardDuration = chapter.id === 'eyes-opened' ? 4.1 : 5.8;
  chapterCard.classList.toggle('is-visible', chapterLocal < chapterCardDuration && !closingVisible);
  closingCard.classList.toggle('is-visible', closingVisible);
  for (const button of chapterList.querySelectorAll<HTMLButtonElement>('.chapter-button')) {
    button.setAttribute('aria-current', String(Number(button.dataset.time) === chapter.start));
  }

  const cue = dialogueAt(currentTime);
  const visual = chapter.visualCues.find((item) => currentTime >= item.start && currentTime < item.end)
    ?? chapter.shots.find((item) => currentTime >= item.start && currentTime < item.end);
  masterAudio.sync(currentTime, playing);
  if (cue) {
    const phrase = cue.subtitlePhrases.find((item) => currentTime >= item.start && currentTime < item.end);
    subtitleSpeaker.textContent = cue.speaker;
    subtitleText.textContent = phrase?.text ?? cue.text;
    sourcePill.textContent = visual?.classification === 'VISION'
      ? `Interpretive visual · VISION  |  Spoken: ${cue.classification} · ${cue.reference}`
      : `${sourceLabel(cue.classification)} · ${cue.reference}`;
    sourcePill.classList.add('is-visible');
  } else if (visual) {
    sourcePill.textContent = `${sourceLabel(visual.classification)} · ${visual.reference}`;
    sourcePill.classList.add('is-visible');
  } else {
    subtitleSpeaker.textContent = '';
    subtitleText.textContent = '';
    sourcePill.classList.remove('is-visible');
  }
  if (closingVisible) sourcePill.classList.remove('is-visible');
  subtitles.hidden = !captionsEnabled || !cue || closingVisible;
}

function frame(now: number): void {
  if (playing) {
    currentTime = Math.min(filmManifest.runtimeSeconds, playAnchorTime + (now - playAnchorClock) / 1000);
    if (currentTime >= filmManifest.runtimeSeconds) {
      playing = false;
      playButton.textContent = '↺';
      playButton.setAttribute('aria-label', 'Replay film');
      shell.classList.remove('is-playing');
      window.clearTimeout(controlsTimer);
      shell.classList.add('controls-visible');
    }
    engine.render(timelineStateAt(currentTime, reducedMotion));
    updateInterface();
  }
  requestAnimationFrame(frame);
}

beginButton.addEventListener('click', async () => {
  void masterAudio.unlock();
  opening.classList.add('is-dismissed');
  if (webglAvailable) poster.classList.add('is-hidden');
  await engine.whenReady;
  resize();
  await setPlaying(true);
});
playButton.addEventListener('click', () => void setPlaying(!playing));
timeline.addEventListener('input', () => seek(Number(timeline.value)));
muteButton.addEventListener('click', () => setMuted(!muted));
const toggleCaptions = () => {
  captionsEnabled = !captionsEnabled;
  captionsButton.setAttribute('aria-pressed', String(captionsEnabled));
  updateInterface();
};
captionsButton.addEventListener('click', () => {
  if (captionKeyboardActive) return;
  toggleCaptions();
});
// Normalize Space activation across mobile/WebKit keyboard bridges. Handling
// keydown and preventing its default avoids the browser's second native click.
captionsButton.addEventListener('keydown', (event) => {
  if (event.code !== 'Space' && event.key !== ' ' && event.key !== 'Spacebar') return;
  event.preventDefault();
  event.stopPropagation();
  if (!captionKeyboardActive) toggleCaptions();
  captionKeyboardActive = true;
});
captionsButton.addEventListener('keyup', (event) => {
  if (event.code !== 'Space' && event.key !== ' ' && event.key !== 'Spacebar') return;
  event.preventDefault();
  event.stopPropagation();
  window.setTimeout(() => { captionKeyboardActive = false; }, 0);
});
motionButton.addEventListener('click', () => {
  reducedMotion = !reducedMotion;
  engine.setReducedMotion(reducedMotion);
  motionButton.setAttribute('aria-pressed', String(reducedMotion));
});
qualitySelect.addEventListener('change', () => {
  const quality = qualitySelect.value as QualityTier;
  engine.setQuality(quality);
  localStorage.setItem('veil-quality', quality);
  void engine.seekFilmTime(currentTime);
});
fullscreenButton.addEventListener('click', () => {
  if (!document.fullscreenElement) void shell.requestFullscreen();
  else void document.exitFullscreen();
});
drawerToggle.addEventListener('click', () => setDrawer(!drawer.classList.contains('is-open')));
drawerClose.addEventListener('click', () => setDrawer(false));
for (const button of chapterList.querySelectorAll<HTMLButtonElement>('.chapter-button')) {
  button.addEventListener('click', () => { seek(Number(button.dataset.time)); setDrawer(false); });
}
window.addEventListener('resize', resize);
window.addEventListener('pointermove', (event) => {
  if (event.pointerType === 'mouse') showControls();
});
document.addEventListener('pointerdown', () => shell.classList.remove('keyboard-navigation'), true);
shell.addEventListener('pointerenter', (event) => {
  if (event.pointerType === 'mouse') showControls();
});
shell.addEventListener('pointerdown', (event) => {
  if (!playing) return;
  const target = event.target as HTMLElement;
  if (target.closest('button, input, select, .drawer, .transport, .topbar')) return;
  if (!shell.classList.contains('controls-visible')) {
    showControls();
  } else if (event.pointerType !== 'mouse') {
    shell.classList.remove('controls-visible');
    window.clearTimeout(controlsTimer);
  } else {
    showControls();
  }
});
shell.addEventListener('focusin', () => showControls());
document.addEventListener('visibilitychange', () => { if (document.hidden && playing) void setPlaying(false); });
document.addEventListener('keydown', (event) => {
  shell.classList.add('keyboard-navigation');
  showControls(5000);
  if (event.key === 'Escape') {
    setDrawer(false);
    return;
  }
  const target = event.target instanceof HTMLElement ? event.target : null;
  if (target?.closest('button, input, select, a, [contenteditable="true"]')) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (event.code === 'Space') { event.preventDefault(); void setPlaying(!playing); }
  if (event.code === 'ArrowLeft') seek(currentTime - 5);
  if (event.code === 'ArrowRight') seek(currentTime + 5);
  if (event.key.toLowerCase() === 'c') captionsButton.click();
  if (event.key.toLowerCase() === 'm') muteButton.click();
});
window.addEventListener('beforeunload', () => {
  removeRenderBridge();
  masterAudio.dispose();
  engine.dispose();
});

await engine.whenReady;
resize();
seek(0);
requestAnimationFrame(frame);

declare global {
  interface Window {
    __VEIL_APP__?: { seek(seconds: number): void; play(): Promise<void>; pause(): Promise<void> };
  }
}
window.__VEIL_APP__ = { seek, play: () => setPlaying(true), pause: () => setPlaying(false) };
